// What a past run (migration 057) records from paper beyond its units built (Mitch, 2026-11-03):
// - Set aside (migration 069: "we kept 50 kits in stock with Elecrow for their web-store … We do not personally need to
//   track that inventory, it would just be a cost or similar, but still worth noting that we made 1k and 50 got set
//   aside"): of each variant's units built, those that weren't HHL's to sell, typed in the Units table (RunUnits.tsx).
//   Nobody tracks them as stock; their cost is in the cost per unit of the units kept (shared/costs.ts runUnits()).
// - A PO from paper (po.record_paper): the make PO for the builds, or a purchase or service PO for what was bought for
//   the run, recorded as received on its date: never sent, emailed or incoming in Shopify, never open for planning.

import { FileText, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { roundingText, transferLike, transferVendor } from "../../../../../shared/bills.ts";
import { numberOf } from "../../../../../shared/calc.ts";
import { runResult } from "../../../../../shared/costs.ts";
import { planToday } from "../../../../../shared/day.ts";
import type { ProductionRunLine } from "../../../../../shared/db.gen.ts";
import { useBillIndex, useCanPay } from "../../../lib/bills.ts";
import { centsText, useCostIndex } from "../../../lib/costs.ts";
import { money, qty } from "../../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../../lib/mutate.ts";
import { useRows } from "../../../lib/sync.ts";
import { uploadAttachment } from "../../../ui/Attachments.tsx";
import { Button, CommitInput, cx, Input, Select } from "../../../ui/controls.tsx";
import { type Column, DataTable } from "../../../ui/DataTable.tsx";
import { FileDrop } from "../../../ui/FileDrop.tsx";
import { toast } from "../../../ui/toast.tsx";
import { PaidWith } from "../../bills/PaidWith.tsx";
import { ItemPicker, Modal } from "../../consignment/parts.tsx";
import { Card } from "../Workspace.tsx";
import type { RunProps } from "./RunWorkspace.tsx";

/** A whole number of units, saved when it's left (Enter or blur; Esc puts it back): the run's unit grids and Set aside. */
// A PO from paper ------------------------------------------------------------------------------------------------

type Kind = "make" | "purchase" | "service";
/**
 * Where a line's price came from (Mitch, 2026-11-03: "why can that not come from the actual part and BOM we have set up?"):
 * the BOM (a build: its maker's charges, else what the BOM says the maker charges), the vendor's price list, the plan (a
 * part's usual price), or typed. A build left at its BOM's price is saved without one, so the line takes the BOM's
 * charges as Create make PO does (its cost splits into materials and assembly).
 */
type PriceFrom = "bom" | "vendor" | "plan" | "";
type Line = { id: string; item_id: string; qty: string; price: string; from: PriceFrom };
const FROM_LABEL: Record<Exclude<PriceFrom, "">, string> = { bom: "BOM", vendor: "price list", plan: "plan" };
const FROM_TITLE: Record<Exclude<PriceFrom, "">, string> = {
  bom: "From its BOM: what the maker charges for it (its charges, else the parts it buys)",
  vendor: "From the vendor's price list",
  plan: "The plan's price for this part (its usual cost)",
};
const priceText = (n: number) => String(Math.round(n * 10_000) / 10_000);

const KINDS: { value: Kind; label: string; says: (cm: string) => string }[] = [
  {
    value: "make",
    label: "The build (make PO)",
    says: (cm) => `What ${cm || "the CM"} charged to build this run: one line per product, at its price per unit`,
  },
  { value: "purchase", label: "Parts or materials", says: () => "What HHL bought for this run, sent to the CM or not" },
  { value: "service", label: "A service", says: () => "Testing, certification, design work… bought on a PO" },
];

export function PaperPoButton({ run }: { run: RunProps["run"] }) {
  const [open, setOpen] = useState(false);
  if (!can("po.record_paper")) return null;
  return (
    <>
      <Button
        size="sm"
        icon={<FileText className="size-3.5" />}
        onClick={() => setOpen(true)}
        title="A PO from before HHL Ops: recorded as received, never sent"
      >
        Record a paper PO
      </Button>
      {open && <PaperPoDialog run={run} onClose={() => setOpen(false)} />}
    </>
  );
}

function PaperPoDialog({ run, onClose }: { run: RunProps["run"]; onClose: () => void }) {
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const vendors = useRows("vendor");
  const links = useRows("vendor_item");
  const runLines = (cx_.linesByRun.get(run.id) ?? []).filter((l) => Number(l.qty) > 0);
  const hasMake = (cx_.posByRun.get(run.id) ?? []).some((p) => p.kind === "make" && p.status !== "cancelled");
  // A line's price as HHL Ops knows it: a build's from its BOM (its maker's charges, else the vendor's build price, else
  // the parts the maker buys and services), a part's from the vendor's price list, else the plan's.
  const priceOf = (k: Kind, vendorId: string, itemId: string): { price: string; from: PriceFrom } => {
    const link = links.find((v) => v.vendor_id === vendorId && v.item_id === itemId)?.unit_price;
    if (k === "make") {
      const stack = res?.planned.get(itemId);
      const charges = stack?.lines.filter((l) => l.charge) ?? [];
      if (charges.length) return { price: priceText(charges.reduce((n, l) => n + (l.usd ?? 0), 0)), from: "bom" };
      if (link != null) return { price: String(link), from: "vendor" };
      const fee = stack?.lines.filter((l) => l.inFee && !l.charge).reduce((n, l) => n + (l.usd ?? 0), 0) ?? 0;
      return fee > 0 ? { price: priceText(fee), from: "bom" } : { price: "", from: "" };
    }
    if (link != null) return { price: String(link), from: "vendor" };
    for (const s of res?.planned.values() ?? []) {
      const l = s.lines.find((x) => x.itemId === itemId && x.unit != null);
      if (l?.unit != null) return { price: priceText(l.unit), from: "plan" };
    }
    return { price: "", from: "" };
  };
  const buildLines = (vendorId: string): Line[] =>
    runLines.map((l) => ({ id: crypto.randomUUID(), item_id: l.item_id, qty: String(Number(l.qty)), ...priceOf("make", vendorId, l.item_id) }));
  const firstKind: Kind = hasMake ? "purchase" : "make";
  const [kind, setKind] = useState<Kind>(firstKind);
  const [vendorId, setVendorId] = useState(firstKind === "make" ? (run.cm_vendor_id ?? "") : "");
  const [lines, setLines] = useState<Line[]>(firstKind === "make" ? buildLines(run.cm_vendor_id ?? "") : []);
  const [ref, setRef] = useState("");
  const [dated, setDated] = useState(String(run.started_on ?? run.finished_on ?? planToday()).slice(0, 10));
  const [receivedOn, setReceivedOn] = useState(""); // When it all arrived (migration 078): how long it took
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<{ key: string; file: File }[]>([]);
  const addFiles = (list: Iterable<File>) => setFiles((fs) => [...fs, ...[...list].map((file) => ({ key: crypto.randomUUID(), file }))]);
  const [saving, setSaving] = useState(false);
  const vendor = vendors.find((v) => v.id === vendorId);
  const cm = vendors.find((v) => v.id === run.cm_vendor_id);
  const cmName = cm?.short_name || cm?.name || "";

  // Parts HHL supplied for the builds (the run's own supply, else the BOM's): what a parts PO is usually for.
  const sent = useMemo(() => {
    const by = new Map<string, { itemId: string; sku: string; qty: number }>();
    for (const v of res?.variants ?? []) {
      const units = res?.units.get(v)?.built ?? 0;
      for (const l of res?.planned.get(v)?.lines ?? []) {
        if (!l.itemId || l.charge || l.service || l.inFee || l.supply !== "hhl_consigns") continue;
        const had = by.get(l.itemId) ?? { itemId: l.itemId, sku: l.sku, qty: 0 };
        had.qty += l.qty * units;
        by.set(l.itemId, had);
      }
    }
    return [...by.values()].sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true }));
  }, [res]);

  const pickKind = (k: Kind) => {
    setKind(k);
    if (k === "make") {
      const id = run.cm_vendor_id ?? vendorId;
      setVendorId(id);
      setLines(buildLines(id));
    } else if (kind === "make") setLines([]);
  };
  const pickVendor = (id: string) => {
    setVendorId(id);
    setLines((ls) => ls.map((l) => (l.price && !l.from ? l : { ...l, ...priceOf(kind, id, l.item_id) })));
  };
  const add = (itemId: string, n = 0) => {
    if (lines.some((l) => l.item_id === itemId)) return toast.error("That item is on the PO already");
    setLines((ls) => [
      ...ls,
      { id: crypto.randomUUID(), item_id: itemId, qty: n ? String(Math.round(n * 1000) / 1000) : "", ...priceOf(kind, vendorId, itemId) },
    ]);
  };
  const setLine = (id: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const parsed = lines.map((l, i) => ({ ...l, pos: i + 1, n: numberOf(l.qty), p: l.price.trim() === "" ? null : numberOf(l.price) }));
  const total = parsed.reduce((s, l) => s + (l.n > 0 && l.p != null && Number.isFinite(l.p) ? l.n * l.p : 0), 0);
  const unpriced = parsed.filter((l) => l.p == null).length;

  // How it was paid --- (Mitch, 2026-11-03: "ensure that payments are accounted for and linked"): a Wise transfer to
  // this vendor still waiting in Wise history (the ones naming the PI first, then the nearest amount), or paid outside.
  const canPay = useCanPay();
  const bix = useBillIndex();
  const transfers = useRows("wise_transfer");
  const [how, setHow] = useState<"none" | "wise" | "outside">("none");
  const [transferId, setTransferId] = useState<string | null>(null);
  const [payMethod, setPayMethod] = useState<"card" | "wire" | "other">("card");
  const [payAccount, setPayAccount] = useState<string | null>(null);
  const [payVia, setPayVia] = useState("");
  const [paidOn, setPaidOn] = useState(dated);
  const [payRef, setPayRef] = useState("");
  const vendorTransfers = useMemo(() => {
    const refKey = ref.trim().toLowerCase();
    return transfers
      .filter((t) => !t.payment_id && t.state === "outgoing_payment_sent" && t.match_status !== "ignored")
      .filter((t) => transferVendor(bix, transferLike(t))?.vendorId === vendorId)
      .map((t) => ({ t, named: !!refKey && t.reference.toLowerCase().includes(refKey), off: Math.abs(Number(t.amount) - total) }))
      .sort((a, b) => Number(b.named) - Number(a.named) || a.off - b.off)
      .slice(0, 8);
  }, [transfers, bix, vendorId, ref, total]);
  const picked = vendorTransfers.find((x) => x.t.id === transferId)?.t;
  const fitText = (amount: number) => {
    const diff = Math.round((amount - total) * 100) / 100;
    if (Math.abs(diff) < 0.005) return "the PO's total";
    if (Math.abs(diff) <= 1) return roundingText(diff);
    return diff < 0
      ? `part of it (${money(Math.abs(diff), "USD")} left to pay)`
      : `${money(diff, "USD")} more than the PO: link it in Wise history with Choose…`;
  };
  const problem = !vendorId
    ? "Choose the vendor"
    : !lines.length
      ? "Add the lines from the paper"
      : parsed.some((l) => !(l.n > 0))
        ? "Each line needs its quantity"
        : parsed.some((l) => l.p != null && !(Number.isFinite(l.p) && l.p >= 0))
          ? "A price is a number, 0 or more"
          : !dated
            ? "Say when it was dated"
            : dated > planToday()
              ? "A PO from paper is dated today or earlier"
              : receivedOn && receivedOn < dated
                ? "It can't have arrived before it was ordered"
                : how !== "none" && unpriced
                  ? "Give every line its price to record how it was paid"
                  : how === "wise" && !picked
                    ? "Pick the Wise transfer that paid it"
                    : how === "wise" && picked && Number(picked.amount) - total > 1
                      ? "That transfer is more than the PO: record the PO without it, then link it in Wise history with Choose…"
                      : how === "outside" && !paidOn
                        ? "Say when it was paid"
                        : "";

  const save = async () => {
    if (problem) return toast.error(problem);
    setSaving(true);
    const id = crypto.randomUUID();
    const { ok, result } = await mutateWithResult("po.record_paper", {
      id,
      run_id: run.id,
      vendor_id: vendorId,
      kind,
      vendor_ref: ref.trim(),
      dated_on: dated,
      ...(receivedOn ? { received_on: receivedOn } : {}),
      notes: notes.trim(),
      // A build at its BOM's price goes without one, so the server prices it as Create make PO does and copies the charges.
      lines: parsed.map((l) => ({ id: l.id, item_id: l.item_id, qty: l.n, ...(kind === "make" && l.from === "bom" ? {} : { unit_price: l.p }) })),
      ...(how === "wise" && transferId ? { paid: { how: "wise" as const, transfer_id: transferId } } : {}),
      ...(how === "outside"
        ? {
            paid: {
              how: "outside" as const,
              method: payMethod,
              pay_account_id: payAccount,
              paid_via: payVia as never,
              paid_on: paidOn,
              reference: payRef.trim(),
            },
          }
        : {}),
    });
    if (!ok) return setSaving(false);
    const number = (result as { number?: string } | undefined)?.number ?? "The PO";
    let attached = 0;
    for (const { file: f } of files) {
      try {
        await uploadAttachment({ type: "po", id }, f, { docDate: dated });
        attached++;
      } catch (e) {
        toast.error((e as Error).message);
      }
    }
    toast.ok(
      `${number} recorded from paper${how === "wise" ? ", paid by its Wise transfer" : how === "outside" ? ", and paid" : ""}${attached ? `, with ${attached === 1 ? "its document" : `${attached} documents`}` : ""}`,
    );
    onClose();
  };

  // The lines as the paper has them (in the order they were added), typed into like a sheet.
  type Parsed = (typeof parsed)[number];
  const amountOf = (l: Parsed) => (l.n > 0 && l.p != null && Number.isFinite(l.p) ? Math.round(l.n * l.p * 100) / 100 : null);
  const lineColumns: Column<Parsed>[] = [
    {
      key: "pos",
      header: "#",
      width: "44px",
      align: "right",
      cell: (l) => <span className="text-[12px] text-faint">{l.pos}</span>,
    },
    {
      key: "sku",
      header: "SKU",
      width: "140px",
      value: (l) => cx_.itemById.get(l.item_id)?.sku,
      cell: (l) => <span className="truncate font-mono text-[12px]">{cx_.itemById.get(l.item_id)?.sku}</span>,
    },
    {
      key: "item",
      header: "Name",
      width: "minmax(160px,1fr)",
      value: (l) => cx_.itemById.get(l.item_id)?.name,
      cell: (l) => (
        <span className="truncate text-[12px]" title={cx_.itemById.get(l.item_id)?.name}>
          {cx_.itemById.get(l.item_id)?.name}
        </span>
      ),
    },
    {
      key: "qty",
      header: "Qty",
      width: "100px",
      align: "right",
      value: (l) => l.n,
      text: (l) => l.qty,
      cell: (l) => (l.qty.trim() ? <span className="font-mono text-[12px]">{l.qty}</span> : <span className="text-faint">—</span>),
      edit: { get: (l) => l.qty, save: (l, text) => setLine(l.id, { qty: text }) },
    },
    {
      key: "price",
      header: `Unit price${vendor ? ` (${vendor.currency})` : ""}`,
      width: "130px",
      align: "right",
      value: (l) => l.p,
      text: (l) => l.price,
      cell: (l) => (l.price.trim() ? <span className="font-mono text-[12px]">{l.price}</span> : <span className="text-faint">not known</span>),
      edit: { get: (l) => l.price, save: (l, text) => setLine(l.id, { price: text, from: "" }) },
    },
    {
      key: "amount",
      header: "Amount",
      width: "110px",
      align: "right",
      value: amountOf,
      cell: (l) => <span className="font-mono text-[12px]">{amountOf(l) != null && vendor ? money(amountOf(l), vendor.currency) : "—"}</span>,
    },
    {
      key: "from",
      header: "Price from",
      headerTitle: "Where a price filled in by HHL Ops came from (empty: typed from the paper)",
      width: "96px",
      value: (l) => (l.from ? FROM_LABEL[l.from] : ""),
      cell: (l) =>
        l.from && (
          <span className="truncate text-[11px] text-faint" title={FROM_TITLE[l.from]}>
            {FROM_LABEL[l.from]}
          </span>
        ),
    },
    {
      key: "remove",
      header: "",
      width: "40px",
      value: () => "",
      cell: (l) => (
        <button
          type="button"
          title="Take this line off"
          onClick={(e) => {
            e.stopPropagation();
            setLines((ls) => ls.filter((x) => x.id !== l.id));
          }}
          className="rounded p-1 text-faint hover:bg-hover hover:text-text"
        >
          <Trash2 className="size-3.5" />
        </button>
      ),
    },
  ];
  return (
    <Modal
      title={`Record a paper PO on ${run.number}`}
      onClose={onClose}
      width={860}
      footer={
        <>
          <span className="text-[12px] text-muted">
            Recorded as received on its date: nothing is sent to {vendor?.short_name || vendor?.name || "the vendor"} or Shopify, and planning doesn't count it.
          </span>
          <Button className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={saving || !!problem} title={problem || undefined} onClick={save}>
            {saving ? "Recording…" : "Record the PO"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-3 gap-2">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => pickKind(k.value)}
              className={cx(
                "rounded-lg border px-3 py-2 text-left",
                kind === k.value ? "border-accent bg-accent-soft/40" : "border-line hover:border-line-strong hover:bg-hover",
              )}
            >
              <div className="text-[13px] font-medium">{k.label}</div>
              <div className="mt-0.5 text-[11px] text-muted">{k.says(cmName)}</div>
            </button>
          ))}
        </div>
        {kind === "make" && hasMake && (
          <div className="rounded-md border border-warn/40 bg-warn-soft px-3 py-2 text-[12px]">
            This run has a make PO already: record another only if the CM billed the builds twice.
          </div>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)_200px_160px] gap-3">
          <label className="flex flex-col gap-1 text-[12px] text-muted">
            Vendor
            <Select
              aria-label="Vendor"
              value={vendorId}
              onChange={(e) => pickVendor(e.target.value)}
              options={[
                { value: "", label: "Choose…" },
                ...vendors
                  .filter((v) => !v.archived && (kind !== "make" || v.kind !== "supplier"))
                  .sort((a, b) => (a.short_name || a.name).localeCompare(b.short_name || b.name))
                  .map((v) => ({ value: v.id, label: v.short_name || v.name })),
              ]}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-muted">
            Their PI or invoice number
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. PI-20260804" />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-muted">
            Ordered on
            <Input type="date" value={dated} max={planToday()} onChange={(e) => setDated(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-muted" title="The day it all arrived: with the day it was ordered, how long it took">
            Received on
            <Input type="date" value={receivedOn} min={dated} max={planToday()} onChange={(e) => setReceivedOn(e.target.value)} />
          </label>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium">Lines</span>
          </div>
          <DataTable
            fit={8}
            editing
            rows={parsed}
            columns={lineColumns}
            defaultSort={{ key: "pos" }}
            exportName="Paper PO lines"
            pageFilters={false}
            rowNoun={["line", "lines"]}
            empty="Add the lines from the paper"
          />
          <div className="flex items-center gap-3">
            <ItemPicker
              className="w-72"
              allow={(i) => (kind === "make" ? i.kind === "sell" || i.kind === "subassembly" : true)}
              onPick={(i) => add(i.id)}
              placeholder="Add a line: SKU or name…"
            />
            {vendor && (
              <span className="ml-auto text-[12px] text-muted">
                Total <span className="font-mono text-text">{money(Math.round(total * 100) / 100, vendor.currency)}</span>
                {unpriced ? ` · ${unpriced} without a price` : ""}
                {parsed.some((l) => l.from) ? " · prices filled in from HHL Ops: check each against the paper" : ""}
              </span>
            )}
          </div>
          {kind === "purchase" && sent.some((s) => !lines.some((l) => l.item_id === s.itemId)) && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted">HHL supplied for these builds:</span>
              {sent
                .filter((s) => !lines.some((l) => l.item_id === s.itemId))
                .map((s) => (
                  <button
                    key={s.itemId}
                    type="button"
                    onClick={() => add(s.itemId, s.qty)}
                    className="inline-flex h-6 items-center gap-1 rounded border border-line px-1.5 font-mono text-[11px] hover:border-line-strong hover:bg-hover"
                  >
                    <Plus className="size-3" />
                    {s.sku} × {qty(Math.round(s.qty * 1000) / 1000)}
                  </button>
                ))}
            </div>
          )}
        </div>

        {canPay && (
          <div className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-medium">How was it paid?</span>
              {(
                [
                  ["none", "Not recorded here"],
                  ["wise", "A Wise transfer"],
                  ["outside", "Card, PayPal or wire"],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setHow(k)}
                  className={cx(
                    "h-7 rounded-md border px-2.5 text-[12px]",
                    how === k ? "border-accent bg-accent-soft/40 font-medium" : "border-line hover:border-line-strong hover:bg-hover",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {how === "wise" && (
              <div className="mt-2 flex flex-col">
                {vendorTransfers.length ? (
                  vendorTransfers.map(({ t, named }) => (
                    <label key={t.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-hover">
                      <input type="radio" name="transfer" checked={transferId === t.id} onChange={() => setTransferId(t.id)} />
                      <span className="w-24 text-muted">{t.transfer_date ? String(t.transfer_date).slice(0, 10) : ""}</span>
                      <span className="w-28 text-right font-mono">{money(Number(t.amount), t.currency)}</span>
                      <span className="min-w-0 flex-1 truncate font-mono text-muted">{t.reference}</span>
                      {named && <span className="shrink-0 text-[11px] text-ok">names the PI</span>}
                      <span className="w-56 shrink-0 truncate text-right text-[11px] text-faint">{fitText(Number(t.amount))}</span>
                    </label>
                  ))
                ) : (
                  <span className="text-[12px] text-muted">
                    No Wise transfer to {vendor?.short_name || vendor?.name || "this vendor"} is waiting in Wise history. If one paid it, link the vendor's Wise
                    recipient on its panel, or link the transfer from Wise history after recording the PO.
                  </span>
                )}
              </div>
            )}
            {how === "outside" && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1 text-[12px]">
                  <span className="text-muted">How</span>
                  <Select
                    aria-label="How it was paid"
                    value={payMethod}
                    onChange={(e) => {
                      setPayMethod(e.target.value as "card" | "wire" | "other");
                      setPayAccount(null);
                      setPayVia("");
                    }}
                    options={[
                      { value: "card", label: "Card (or PayPal)" },
                      { value: "wire", label: "Bank wire" },
                      { value: "other", label: "Other" },
                    ]}
                  />
                </label>
                <label className="flex flex-col gap-1 text-[12px]">
                  <span className="text-muted">Paid on</span>
                  <Input type="date" value={paidOn} max={planToday()} onChange={(e) => setPaidOn(e.target.value)} />
                </label>
                <label className="flex flex-col gap-1 text-[12px]">
                  <span className="text-muted">Their order or transaction number</span>
                  <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="e.g. 317104" className="font-mono" />
                </label>
                <PaidWith
                  method={payMethod}
                  accountId={payAccount}
                  via={payVia}
                  onChange={(p) => {
                    if (p.accountId !== undefined) setPayAccount(p.accountId);
                    if (p.via !== undefined) setPayVia(p.via);
                  }}
                  className="col-span-3 grid grid-cols-2 gap-2"
                />
                <span className="col-span-3 text-[11px] text-faint">
                  Makes the PO's bill ({money(Math.round(total * 100) / 100, vendor?.currency ?? "USD")}) and its payment, paid in full.
                </span>
              </div>
            )}
          </div>
        )}

        {/* The shared file box (ui/FileDrop.tsx): while the dialog is open, a file dragged anywhere on the page is for it
            (Mitch, 2026-11-03: "there wasn't a clear highlighted area when I was dragging/dropping a pdf"): the box lights up
            and comes into view, and the browser never opens the PDF over the half-filled form */}
        <FileDrop
          anywhere
          size="sm"
          title="The PI, invoice or payment receipt"
          onFiles={addFiles}
          chosen={files.map((f) => f.file)}
          onRemove={(i) => setFiles((fs) => fs.filter((_, j) => j !== i))}
        />

        <label className="flex flex-col gap-1 text-[12px] text-muted">
          Notes
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={`Recorded from paper for ${run.number}`} />
        </label>
      </div>
    </Modal>
  );
}
